import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Selector } from "../../../Common/_components/Selector";
import { CapsuleButton } from "../../_components/CapsuleButton";
import { CustomDropdown } from "../../_components/CustomDropdown";
import { CustomModal } from "../../_components/CustomModal";

interface InvitationGroupOption {
    uid: string;
    name: string;
}

interface RoomOption {
    uid: string;
    name: string;
}

type AccessScope = "LOCATION" | "ROOMS";

interface Props {
    groups: InvitationGroupOption[];
    rooms: RoomOption[];
    selectedGroupUid: string | null;
    onChange: (invitationGroupUid: string) => void;
    onCreate: (params: {
        name: string;
        accessScope: AccessScope;
        roomUids: string[];
    }) => Promise<unknown>;
    // When true, hide the inline banner once a group is configured. The parent
    // is then responsible for opening the modal via `openSignal`.
    hideBannerWhenConfigured?: boolean;
    // Incrementing this value from the parent opens the modal (e.g. from a
    // settings dropdown). Leave undefined when driving the component solely
    // via the inline banner.
    openSignal?: number;
}

type Mode = "pick" | "create";

export function ParkingGroupConfig({
    groups,
    rooms,
    selectedGroupUid,
    onChange,
    onCreate,
    hideBannerWhenConfigured = false,
    openSignal,
}: Props) {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [mode, setMode] = useState<Mode>("pick");
    const [draftUid, setDraftUid] = useState<string | null>(selectedGroupUid);

    const [newName, setNewName] = useState("");
    const [newAccessScope, setNewAccessScope] = useState<AccessScope>("ROOMS");
    const [newRoomUids, setNewRoomUids] = useState<string[]>([]);
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const selectedGroup = useMemo(
        () => groups.find((g) => g.uid === selectedGroupUid) ?? null,
        [groups, selectedGroupUid],
    );

    const options = useMemo(
        () => groups.map((g) => CustomDropdown.buildOption(g.uid, g.name)),
        [groups],
    );

    const roomOptions = useMemo(
        () => rooms.map((r) => ({ value: r.uid, label: r.name })),
        [rooms],
    );

    const hasGroups = groups.length > 0;

    const openModal = () => {
        setDraftUid(selectedGroupUid);
        setNewName("");
        setNewAccessScope("ROOMS");
        setNewRoomUids([]);
        setCreateError(null);
        setMode(hasGroups ? "pick" : "create");
        setShowModal(true);
    };

    // Parent-driven open (e.g. from a settings dropdown). First render has
    // `openSignal` undefined, so we skip; any change thereafter opens the modal.
    useEffect(() => {
        if (openSignal === undefined) return;
        openModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openSignal]);

    const closeModal = () => {
        setShowModal(false);
        setIsCreating(false);
    };

    const confirmPick = () => {
        if (draftUid && draftUid !== selectedGroupUid) onChange(draftUid);
        closeModal();
    };

    const isCreateValid =
        newName.trim().length > 0 &&
        (newAccessScope === "LOCATION" || newRoomUids.length > 0);

    const submitCreate = async () => {
        if (!isCreateValid || isCreating) return;
        setIsCreating(true);
        setCreateError(null);
        try {
            await onCreate({
                name: newName.trim(),
                accessScope: newAccessScope,
                roomUids: newAccessScope === "ROOMS" ? newRoomUids : [],
            });
            closeModal();
        } catch (e: any) {
            setCreateError(
                e?.message ?? t("mod_parking_group_create_error"),
            );
        } finally {
            setIsCreating(false);
        }
    };

    // Banner eliminado a propósito: el empty-state central cubre el caso
    // "sin grupo" y el modal se abre desde el menú Ajustes.
    const banner = null;

    const modeToggle =
        mode === "pick" ? (
            <button
                type="button"
                className="btn btn-link p-0 mb-2"
                onClick={() => setMode("create")}
            >
                {t("mod_parking_group_create_link")}
            </button>
        ) : hasGroups ? (
            <button
                type="button"
                className="btn btn-link p-0 mb-2"
                onClick={() => setMode("pick")}
            >
                {t("mod_parking_group_pick_link")}
            </button>
        ) : null;

    return (
        <>
            {banner}
            <CustomModal
                show={showModal}
                centered
                onHide={closeModal}
                title={
                    selectedGroup
                        ? t("mod_parking_group_change")
                        : t("mod_parking_group_configure")
                }
                content={
                    <div>
                        {modeToggle}
                        {mode === "pick" ? (
                            <>
                                <p>{t("mod_parking_group_picker_intro")}</p>
                                <CustomDropdown
                                    title={t("mod_parking_group_label")}
                                    options={options}
                                    value={draftUid ?? ""}
                                    onChange={(value: string) =>
                                        setDraftUid(value)
                                    }
                                    sortAlphabetically
                                />
                            </>
                        ) : (
                            <>
                                <p>{t("mod_parking_group_create_intro")}</p>
                                <div className="form-group">
                                    <label className="mb-1">
                                        {t("mod_parking_group_create_name")}
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={newName}
                                        onChange={(e) =>
                                            setNewName(e.target.value)
                                        }
                                        placeholder={t(
                                            "mod_parking_group_create_name_placeholder",
                                        )}
                                        maxLength={80}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="mb-1">
                                        {t("mod_parking_group_create_scope")}
                                    </label>
                                    <div className="form-check">
                                        <input
                                            type="radio"
                                            className="form-check-input"
                                            id="parking-scope-location"
                                            checked={
                                                newAccessScope === "LOCATION"
                                            }
                                            onChange={() =>
                                                setNewAccessScope("LOCATION")
                                            }
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor="parking-scope-location"
                                        >
                                            {t(
                                                "mod_parking_group_create_scope_location",
                                            )}
                                        </label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            type="radio"
                                            className="form-check-input"
                                            id="parking-scope-rooms"
                                            checked={
                                                newAccessScope === "ROOMS"
                                            }
                                            onChange={() =>
                                                setNewAccessScope("ROOMS")
                                            }
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor="parking-scope-rooms"
                                        >
                                            {t(
                                                "mod_parking_group_create_scope_rooms",
                                            )}
                                        </label>
                                    </div>
                                </div>
                                {newAccessScope === "ROOMS" && (
                                    <div className="form-group mb-0">
                                        <label className="mb-1">
                                            {t(
                                                "mod_parking_group_create_rooms",
                                            )}
                                        </label>
                                        {rooms.length === 0 ? (
                                            <p className="text-muted font-italic small mb-0">
                                                {t(
                                                    "mod_parking_group_create_no_rooms",
                                                )}
                                            </p>
                                        ) : (
                                            <Selector
                                                isMulti
                                                hideSelectedOptions={false}
                                                closeMenuOnSelect={false}
                                                placeholder={t(
                                                    "mod_parking_group_create_rooms_placeholder",
                                                )}
                                                options={roomOptions}
                                                value={roomOptions.filter(
                                                    (o) =>
                                                        newRoomUids.includes(
                                                            o.value,
                                                        ),
                                                )}
                                                onChange={(selected: any) =>
                                                    setNewRoomUids(
                                                        (selected ?? []).map(
                                                            (o: any) =>
                                                                o.value,
                                                        ),
                                                    )
                                                }
                                            />
                                        )}
                                    </div>
                                )}
                                <p className="text-muted font-italic small mt-3 mb-0">
                                    {t("mod_parking_group_create_defaults")}
                                </p>
                                {createError && (
                                    <p className="text-danger small mt-2 mb-0">
                                        {createError}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                }
                footer={
                    mode === "pick" ? (
                        <>
                            <CapsuleButton
                                style="DANGER"
                                text={t("global_cancel")}
                                onClick={closeModal}
                            />
                            <CapsuleButton
                                text={t("global_save")}
                                isEnabled={
                                    !!draftUid &&
                                    draftUid !== selectedGroupUid
                                }
                                onClick={confirmPick}
                            />
                        </>
                    ) : (
                        <>
                            <CapsuleButton
                                style="DANGER"
                                text={t("global_cancel")}
                                onClick={closeModal}
                            />
                            <CapsuleButton
                                text={
                                    isCreating
                                        ? t("mod_parking_group_creating")
                                        : t("mod_parking_group_create_submit")
                                }
                                isEnabled={isCreateValid && !isCreating}
                                onClick={submitCreate}
                            />
                        </>
                    )
                }
            />
        </>
    );
}
